import React, { Component } from "react";
import { observer } from "mobx-react";
import { Accessor, ColorX, GFunc, LocaleX, ReqX, ResponsiveX } from "IZOX/STATIC";
import { ActivityIndicator, HStack, VStack, XButton, XText } from "IZOX/_X";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import FooterM from "shared/FooterM";
import Breadcrumb from "shared/Breadcrumb";
import { SysConfig } from "__SYSDefault/Config";

/**
 * @augments {Component<Props, State>}
 */
class PConfirmPolicy extends Component {

  static defaultProps = {}

  constructor(){
    super();
    this.state = {
      loading: true,
      confirming: false,
      confirmed: false,
      error: null,
      info: null
    };
  }

  componentDidMount(){
    this._loadInfo();
  }

  componentDidUpdate(prevProps){
    if(prevProps.location?.search !== this.props.location?.search){
      this._loadInfo();
    }
  }

  _getToken = () => {
    let params = new URLSearchParams(this.props.location?.search ?? "");
    return params.get("token");
  }

  _loadInfo = async () => {
    let token = this._getToken();
    if(!token){
      this.setState({
        loading: false,
        error: {
          EN: "Invalid confirmation link.",
          TC: "確認連結無效。"
        }
      });
      return;
    }

    this.setState({loading: true, error: null});
    let res = await ReqX.SendBE("/Web/PublicPolicy/GetConfirmInfo", {token});
    let {Success, payload} = res;
    if(!Success){
      this.setState({
        loading: false,
        error: {
          EN: payload ?? "Invalid confirmation link.",
          TC: payload ?? "確認連結無效。"
        }
      });
      return;
    }

    this.setState({
      loading: false,
      info: payload,
      confirmed: payload?.alreadyConfirmed
    });
  }

  _confirm = async () => {
    let token = this._getToken();
    if(!token) return;

    this.setState({confirming: true});
    let res = await ReqX.SendBE("/Web/PublicPolicy/Confirm", {token});
    let {Success, payload} = res;
    if(!Success){
      GFunc.Alert(payload ?? "Confirmation failed.", "error");
      this.setState({confirming: false});
      return;
    }

    this.setState({
      confirming: false,
      confirmed: true,
      info: {
        ...this.state.info,
        policyNo: payload?.policyNo ?? this.state.info?.policyNo
      }
    });
  }

  renderBreadcrumb(){
    return (
      <HStack HAlign="[">
        <Breadcrumb
          steps={[{
            caption: {EN: "Home", TC: "主頁"},
            target: "Home"
          }]}
          last={{EN: "Policy Confirmation", TC: "保單確認"}}/>
      </HStack>
    );
  }

  renderContent(){
    let {loading, confirming, confirmed, error, info} = this.state;

    if(loading){
      return (
        <VStack spacing={20} p={40} HAlign="C">
          <ActivityIndicator size="large" color={ColorX.CSS("Axit")}/>
        </VStack>
      );
    }

    if(error){
      return (
        <VStack spacing={20} p={40} HAlign="C">
          <XText bold color={ColorX.CSS("red")} fontSize={18}>
            {LocaleX.Parse(error)}
          </XText>
        </VStack>
      );
    }

    if(confirmed){
      return (
        <VStack spacing={20} p={40} HAlign="C">
          <XText bold color={ColorX.CSS("green")} fontSize={20}>
            {LocaleX.Parse({
              EN: "Policy confirmed successfully",
              TC: "保單已成功確認"
            })}
          </XText>
          <XText color={ColorX.CSS("Stone")}>
            {LocaleX.Parse({
              EN: `Policy Number: ${info?.policyNo ?? ""}`,
              TC: `保單號碼: ${info?.policyNo ?? ""}`
            })}
          </XText>
          <XText color={ColorX.CSS("Gray4")} fontSize={14}>
            {LocaleX.Parse({
              EN: "Your application will now be processed by Mutual Insurance. Thank you.",
              TC: "您的申請現將由衡安保險顧問公司處理。謝謝。"
            })}
          </XText>
        </VStack>
      );
    }

    return (
      <VStack spacing={20} p={40} HAlign="[" fullWidth>
        <XText bold fontSize={22} color={ColorX.CSS("Axit")}>
          {LocaleX.Parse({
            EN: "Confirm Your Policy Application",
            TC: "確認您的保單申請"
          })}
        </XText>
        <XText color={ColorX.CSS("Stone")}>
          {LocaleX.Parse({
            EN: "Please review the details below and confirm that you authorize this insurance application.",
            TC: "請查閱以下詳情，並確認您授權此投保申請。"
          })}
        </XText>
        <VStack spacing={8} p={20} round={10} bW={1} bColor={ColorX.CSS("greyOut")} fullWidth>
          <XText>{LocaleX.Parse({EN: `Policy Number: ${info?.policyNo ?? ""}`, TC: `保單號碼: ${info?.policyNo ?? ""}`})}</XText>
          <XText>{LocaleX.Parse({EN: `Policyholder: ${info?.clientNameEN ?? ""}`, TC: `保單持有人: ${info?.clientNameTC ?? info?.clientNameEN ?? ""}`})}</XText>
          <XText>{LocaleX.Parse({EN: `Agent: ${info?.agencyNameEN ?? ""} (${info?.agencyCode ?? ""})`, TC: `代理: ${info?.agencyNameTC ?? info?.agencyNameEN ?? ""} (${info?.agencyCode ?? ""})`})}</XText>
          <XText>{LocaleX.Parse({EN: `Insured Helper: ${info?.helperName ?? ""}`, TC: `受保家傭: ${info?.helperName ?? ""}`})}</XText>
        </VStack>
        <HStack HAlign="C" fullWidth>
          <XButton
            bgColor={ColorX.CSS("Axit")}
            color={ColorX.CSS("pureWhite")}
            padding={15}
            pH={40}
            round={8}
            loading={confirming}
            onPress={this._confirm}>
            <XText bold color={ColorX.CSS("pureWhite")}>
              {LocaleX.Parse({EN: "Confirm Application", TC: "確認申請"})}
            </XText>
          </XButton>
        </HStack>
      </VStack>
    );
  }

  render(){
    let {0: renderM, [SysConfig.WebUIWidth]: renderW} = ResponsiveX.Render({
      0: () => (
        <VStack fullWidth spacing={20} p={20}>
          {this.renderContent()}
          <FooterM/>
        </VStack>
      ),
      [SysConfig.WebUIWidth]: () => (
        <VStack fullWidth spacing={20} p={40} maxWidth={800} HAlign="C">
          {this.renderBreadcrumb()}
          {this.renderContent()}
        </VStack>
      )
    });

    return renderW ?? renderM;
  }

}

export default withRouter(observer(PConfirmPolicy));
